import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { InteractiveWidget } from '@marketing/interactive-widgets/interactive-widgets.interface';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import {
  PersonalisationAPIService,
  ProfileService,
  SignInService,
  ViewModeService,
  WidenService,
} from '@services';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import { InteractiveWidgetServiceService } from '@marketing/interactive-widgets/interactive-widget-service.service';
import {
  CtaTheme,
  CtaCustomColors,
  AnalyticsService,
  ResponsiveService,
} from '@frk/eds-components';
import { BehaviorSubject, fromEvent, Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { Logger } from '@utils/logger';
import { LinkCollection } from '@types';
import { TranslateService } from '@shared/translate/translate.service';
import { WINDOW } from '@ng-web-apis/common';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import snakeCase from 'lodash/snakeCase';

const logger = Logger.getLogger('USServicingLatestDocumentsCardComponent');

interface InteractiveWidgetsParams {
  document?: string;
  theme?: CtaTheme;
  addPadding?: boolean;
  experiment?: string;
  variation?: string;
  customBgColor?: CtaCustomColors;
}
// TODO: Widget Types can be defined in Bloomreach component. For now this component is used only for PCS which is set as default.
// We can add more types when new use cases will be defined
enum WidgetType {
  PCS = 'PCS',
}

@Component({
  selector: 'ft-interactive-widgets',
  templateUrl: './interactive-widgets.component.html',
  styleUrls: ['./interactive-widgets.component.scss'],
})
export class InteractiveWidgetsComponent
  extends AbstractBaseComponent
  implements OnInit, AfterViewInit {
  @Input() myDashboardComponent?: BrComponent;
  @Input() myDashboardPage?: Page;
  @ViewChild('interactiveWidgetsComponent')
  interactiveWidgetsComponent: ElementRef;
  @ViewChild('buttonContainer', { static: false }) buttonContainer!: ElementRef;
  @ViewChild('btn1', { static: false }) btn1?: ElementRef;
  @ViewChild('btn2', { static: false }) btn2?: ElementRef;
  private unSubscribe$: Subject<void> = new Subject<void>();
  public interactiveWidgets: Array<InteractiveWidget>;
  public interactiveWidgetsParams: InteractiveWidgetsParams;
  public isBypassUser: boolean;
  public isProfileActivated$: Observable<boolean>;
  public widgetType: WidgetType = WidgetType.PCS;
  public widgetTypes = WidgetType;
  public isLoggedIn$: Observable<boolean>;
  public widgetsWidth$: BehaviorSubject<number> = new BehaviorSubject<number>(
    2
  );
  public launchDarklyFlagValue = '';
  public buttonsWrapped$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  constructor(
    private widenService: WidenService,
    private profileService: ProfileService,
    private interactiveWidgetServiceService: InteractiveWidgetServiceService,
    private viewModeService: ViewModeService,
    private launchDarklyService: LaunchDarklyService,
    private personalisationService: PersonalisationAPIService,
    private analyticsService: AnalyticsService,
    private translateService: TranslateService,
    private signInService: SignInService,
    private responsiveService: ResponsiveService,
    @Inject(WINDOW) readonly windowRef: Window
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  ngOnInit(): void {
    this.isLoggedIn$ = this.profileService.isLoggedIn$();
    this.interactiveWidgets = this.getInteractiveWidgets() || [];
    this.isBypassUser = this.profileService.isBypass(true);
    this.isProfileActivated$ = this.personalisationService.isProfileActivated$();
    // checking if the experiment is present or not
    // then getting the flags from launch darkly to show the variation
    // experiment name in BR should be same as the key of feature flag in the launch darkly
    if (this.interactiveWidgetsParams?.experiment) {
      if (this.interactiveWidgets[0]?.isFirmAllowed) {
        this.launchDarklyService
          ?.getFlags$()
          ?.pipe(takeUntil(this.unSubscribe$))
          ?.subscribe((flags: LDFlagSet) => {
            if (flags) {
              this.launchDarklyFlagValue =
                flags[this.interactiveWidgetsParams?.experiment];
            }
          });
      }
    } else {
      // Component with 'experiment' have only one widget now. display tracking will be covered by launchDarklyService
      this.trackingDisplay();
    }

    // Listen for window resize
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(200),
        map(() => this.interactiveWidgetsComponent?.nativeElement?.offsetWidth),
        distinctUntilChanged(),
        takeUntil(this.unSubscribe$)
      )
      .subscribe((width: number) => {
        logger.debug('Component width:', width);
        this.widgetsWidth$.next(width);
        // reseting buttonsWrapped while window rescale
        this.buttonsWrapped$.next(false);
        // additional timeout to allow buttonsWrapped reset
        setTimeout(
          // During window resize containerWidth is counted with gap, which needs to be removed fro counting
          () => this.buttonsWrapped$.next(this.checkButtonsWidth(-8))
        );
      });
  }

  ngAfterViewInit() {
    this.widgetsWidth$.next(
      this.interactiveWidgetsComponent?.nativeElement?.offsetWidth
    );
    // Set columns layout for widgets
    this.widgetsWidth$
      .asObservable()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe((widgetWidth) => {
        this.interactiveWidgets.forEach((widget: InteractiveWidget) => {
          widget.columnLayout$ = this.getColumnsLayout$(widget, widgetWidth);
          logger.debug('Widget:', widget, widgetWidth);
        });
      });
    // additional timeout to allow rener component correctly
    setTimeout(() => this.buttonsWrapped$.next(this.checkButtonsWidth(-8)));
  }

  /**
   * Check Buttons Width
   * @param containerSpace - optionnal number to add container width for button margins
   * @return boolean
   */
  private checkButtonsWidth(containerSpace = 0): boolean {
    if (
      !this.btn1?.nativeElement?.offsetWidth ||
      !this.btn2?.nativeElement?.offsetWidth
    ) {
      return false; // Avoid errors if elements are not available
    }
    const containerWidth = this.buttonContainer?.nativeElement?.offsetWidth;
    const buttonsWidth =
      this.btn1?.nativeElement?.offsetWidth +
      this.btn2?.nativeElement?.offsetWidth;
    return buttonsWidth >= containerWidth + containerSpace;
  }

  /**
   * Get Interactive Widgets array
   */
  private getInteractiveWidgets(): InteractiveWidget[] {
    // Component can be standalone or included in myDashboard
    const component = this.myDashboardComponent
      ? this.myDashboardComponent
      : this.component;
    const page = this.myDashboardPage ? this.myDashboardPage : this.page;

    const interactiveWidgetsModel = component.getModels();
    this.interactiveWidgetsParams = component.getParameters();
    const interactiveWidgets = page
      .getContent(
        interactiveWidgetsModel.InteractiveWidgets ||
          interactiveWidgetsModel.interactiveWidgets ||
          interactiveWidgetsModel.document
      )
      ?.getData()?.interactiveWidget;
    interactiveWidgets?.forEach((widget: InteractiveWidget) => {
      widget.imgSrc = this.widenService.getWidenAssetUrl(
        widget.logoImage?.widenAsset
      );
      widget.interactiveConfigImpl = JSON.parse(widget.interactiveConfig);
      widget.isFirmAllowed = this.interactiveWidgetServiceService.getIsFirmAllowed(
        widget.firmName,
        this.profileService.getUserProfile()
      );
      widget.labelsImpl = interactiveWidgetsModel[widget.labels];
      widget.isDisplayed$ = this.isWidgetDisplayed$(widget?.loggedInOnly);
      widget.theme = widget?.theme || this.interactiveWidgetsParams?.theme;
      widget.customBgColor =
        widget?.customBgColor || this.interactiveWidgetsParams?.customBgColor;
      widget.promoAsset = widget?.additionalPromoAsset?.widenDocument;
      widget.marginBottom =
        widget?.marginBottom && widget?.marginBottom !== 'none'
          ? ' spacing--' + widget?.marginBottom
          : '';
      widget.buttonLink = this.getLinkUrl(page, widget?.button);
      widget.buttonLink2 = this.getLinkUrl(page, widget?.button2);
    });
    return interactiveWidgets;
  }

  /**
   * Check widget display
   * @param loggedInOnly - boolean from widget config
   */
  private isWidgetDisplayed$(loggedInOnly = false): Observable<boolean> {
    if (this.isEditMode) {
      // Display widget in edit mode
      return of(true);
    }
    if (loggedInOnly) {
      return this.isLoggedIn$;
    }
    return of(true);
  }

  /**
   * Get Columns Layout
   * @param widget - InteractiveWidget
   * @param widgetsWidth - component width as number
   * @returns - columns layout as Observable number
   */
  private getColumnsLayout$(
    widget: InteractiveWidget,
    widgetsWidth: number
  ): Observable<number> {
    if (widgetsWidth > 1024 && widget?.additionalPromoAsset) {
      return of(widget?.imgSrc ? 3 : 2);
    }
    return of(widget?.imgSrc ? 2 : 1);
  }

  /**
   * Get Internal Link Url
   * @param page - BR Page object
   * @param button - button linkCollection object
   * @returns - URL string or null
   */
  private getLinkUrl(
    page: Page,
    button: { linkCollection: LinkCollection[] }
  ): string | null {
    if (!button?.linkCollection || button.linkCollection.length === 0) {
      return null;
    }
    if (button.linkCollection[0].url) {
      return button.linkCollection[0].url;
    }
    if (button.linkCollection[0]?.widenAssetCompound?.widenDocument) {
      return this.widenService.getWidenAssetUrl(
        button.linkCollection[0].widenAssetCompound.widenDocument
      );
    }
    const ref = button.linkCollection[0]?.document;
    const url = page?.getContent(ref)?.getUrl();
    return url || null;
  }

  /**
   * Add Password button click
   * @param event - MouseEvent
   */
  public goToAuth0(hint?: string): void {
    logger.debug('activate profile or sign in button click');
    const linkText =
      hint === 'signin'
        ? this.translateService.instant('signin.signIn')
        : this.translateService.instant('signin.fastTrackRegistrationButton');
    this.trackEvent(linkText);
    this.signInService.submitAuth0(hint);
  }

  /**
   * call the GA event for tracking the button clicks
   */
  private trackEvent(
    linkText: string,
    linkUrl?: string,
    trackingEventName?: string
  ): void {
    if (this.interactiveWidgetsParams?.experiment) {
      this.launchDarklyService.trackClick(
        this.interactiveWidgetsParams.experiment
      );
    }
    const eventName = trackingEventName
      ? snakeCase(trackingEventName).toLowerCase()
      : 'callout_click';
    const linkType = (): string => {
      if (trackingEventName) {
        return eventName;
      }
      if (linkUrl) {
        return 'card';
      }
      return 'PAT';
    };

    this.analyticsService.trackEvent({
      event: eventName,
      detailed_event: trackingEventName || 'Callout Clicks',
      event_data: {
        category: '',
        link_id: this.interactiveWidgetsParams?.variation,
        link_text: linkText,
        link_type: 'Standard Link',
        link_url: linkUrl || this.windowRef.location.href,
      },
      link_text: linkText,
      name: linkText,
      link_url: linkUrl || this.windowRef.location.href,
      type: linkType(),
    });
  }

  /**
   * Tracking for widget display
   */
  private trackingDisplay(): void {
    this.interactiveWidgets.forEach((widget: InteractiveWidget): void => {
      if (widget?.trackingDisplay && widget?.isFirmAllowed) {
        widget.isDisplayed$
          ?.pipe(takeUntil(this.unSubscribe$))
          ?.subscribe((isDisplayed: boolean): void => {
            if (isDisplayed) {
              const detailedEvent = `${
                widget.trackingEventName || 'Interactive Widget'
              } Display`;
              this.analyticsService.trackEvent({
                event: snakeCase(detailedEvent),
                detailed_event: detailedEvent,
                event_data: {
                  category: 'interactive_widget',
                },
                type: 'interactive_widget',
              });
            }
          });
      }
    });
  }

  /**
   * track the event for election hub as a callout click event
   */
  public trackClickEvent(
    linkUrl: string,
    linkText: string,
    trackingEventName?: string
  ): void {
    this.trackEvent(linkText, linkUrl, trackingEventName);
  }
}
